import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { DestroyBase } from 'src/app/components/common/ng-destroy-base';
import { ModulesEnum } from 'src/app/navigation/enum';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { RaciNavigationService } from '../../../navigation/raci-navigation.service';
import { ActionStageEnum } from '../../raci/models/enums/action-stage-enum';
import { TaskTypeEnum } from '../../raci/models/enums/task-type-enum';
import { UserDashboardService } from '../services/user-dahsboard.service';
import { EventBusService } from '../../../services/event-bus.service';
import { AppStorageService } from '../../../services/app-storage.service';

/**
 * Handles the user dashboard host component
 */
@Component({
  selector: 'user-dashboard-host',
  templateUrl: './user-dashboard-host.component.html',
  styleUrls: ['./user-dashboard-host.component.css']
})
export class UserDashboardHostComponent extends DestroyBase implements OnInit {
  /**
   * Approval tasks
   */
  approvalTasks: any;

  /**
   * General tasks
   */
  generalTasks: any[];

  /**
 * Represents the valorem tasks.
 */
  valoremTasks: any;

  /**
   * Represents an array of argus tasks.
   */
  argusTasks: any[];

  /**
   * Represents the user tasks.
   */
  userTasks: any;

  /**
   * Represents an array of general tasks' approval history.
   */
  generalTasksApprovalHistory: any[];

  /**
   * Represents the approval matrix of a specific task.
   */
  approvalMatrixOfTask: any;

  /**
   * Represents the approval matrix of general tasks.
   */
  generalTaskApprovalMatrix: any;

  /**
   * Represents the approval matrix of a function task.
   */
  approvalMatrixOfFunctionTask: any;

  /**
   * Represents the property used for grouping.
   */
  groupBy: string;

  /**
   * Represents the current user.
   */
  currentUser: any;

  /**
   * Represents the active task on the user dashboard.
   */
  userDashboardActiveTask: any;

  /**
   * Indicates whether a general task is selected or not.
   */
  isGeneralTaskSelected = false;

  /**
   * Indicates whether the user tasks list needs to be refreshed.
   */
  refreshUserTasksList = false;

  /**
   * Indicates whether the approval matrix should be hidden.
   */
  hideApprovalMatrix = false;

  /**
   * Indicates whether the project details are displayed.
   */
  isProjectDetailsDisplayed = false;

  /**
   * Indicates whether the approval history is displayed.
   */
  isApprovalHistoryDisplayed = false;

  /**
   * Indicates whether the "Publish to Valorem" option is displayed.
   */
  isPublishToValoremDisplayed = false;

  /**
   * Indicates whether the "Get Data from Argus" dialog should be shown.
   */
  showGetDataFromArgusDialog = false;

  /**
   * Represents the approval history.
   */
  approvalHistory: any[];

  /**
   * Represents the selected property of a general task.
   */
  selectedGeneralTaskProperty: any;

  /**
   * Represents the active task.
   */
  activeTask: any;

  /**
   * Indicates whether approving is allowed.
   */
  allowApproving = false;

  /**
   * Represents the properties.
   */
  properties: any[];

  /**
   * Indicates whether the "Sync with Argus" option is displayed.
   */
  isSyncWithArgusDisplayed = false;

  /**
   * Represents the selected report IDs.
   */
  selectedReportIds: any;

  /**
   * Represents the selected properties.
   */
  selectedProperties: any;

  /**
   * Represents the last synchronization information with Argus.
   */
  argusLastSyncInfo: any;

  /**
   * Represents the number of synchronized properties with Argus.
   */
  argusNumberOfSyncProp: any;

  /**
   * Indicates whether the task approval history should be shown.
   */
  shouldShowTaskApprovalHistory = false;

  /**
   * Represents the valuation event tasks.
   */
  valuationEventTasks: any;

  /**
   * Represents the message for the close valuation event dialog.
   */
  closeValEventDialogMessage = "";

  /**
   * Indicates whether the close valuation event dialog should be shown.
   */
  showCloseValuationEventDialog = false;

  /**
   * Represents the active portfolio.
   */
  activePortfolio: any;

  /**
   * Represents the selected option for closing the valuation event.
   */
  closeValEventOptionSelected: number;

  /**
   * Indicates whether the project is closed.
   */
  isProjectClosed = false;

  /**
   * Indicates whether the copy properties option is selected.
   */
  copyProperties = false;

  /**
  * Indicates whether the copy fx table option is selected.
  */
  copyFxTables = false;

  /**
  * Valuation date for the new valuation event
  */
  newValuationDate: any;

  /**
  * Valuation name for the new valuation event
  */
  newValuationName: string;

  /**
   * Indicates whether general task is active.
   */
  isGeneralTaskActive = false;

  /**
   * Array of general tasks approval history.
   */
  generalApprovalHistoryList: any[];

  /**
   * Constructor
   * @param _navigationService
   * @param appStorage
   * @param eventBus
   * @param _raciNavigationService
   * @param _userDashboardService
   * @param _router
   */
  constructor(private _navigationService: NavigationService,
    private appStorage: AppStorageService,
    private eventBus: EventBusService,
    private _raciNavigationService: RaciNavigationService,
    private _userDashboardService: UserDashboardService,
    private _router: Router) {
    super();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.updateNavigationIcons(null);
    this._raciNavigationService.onDisplayApprovalHistory(false);
    this._raciNavigationService.onHideApprovalHistoryIcon(true);
    this.currentUser = this.appStorage.getCurrentUserDetails();
    if (!this.currentUser) {
      this._navigationService.setActiveModule(ModulesEnum.ProjectValuation);
      this._router.navigate(['/pd']);
    }
    else {
      this._navigationService.setActiveModule(ModulesEnum.UserDashboard);
      this.subscriptions.add(this.getData().subscribe(data => {
        if (data) {
          this.setUserDashboardTasks(data);
        }
      }));

      this.onSyncWithArgusAction();
      this.groupPortfolioPropertiesBy();
      this.onDisplayProjectDetailsBroadcast();
      this.onEvents.push(this.onCloseClickBroadcast());
      this.onDisplayHistoryApprovalBroadcast();
      this.onDisplayPublishToValorem();
      this.onEvents.push(this.onPulldataFromArgus());
      this.onSetGeneralTaskActive();
      this.onDisplayTaskHistoryDialog();
      this.onDisplayCloseValuationEventDialog();
    }
  }

  /**
   * Sets user dashboard tasks
   * @param data User dashboard data
   */
  setUserDashboardTasks(data: any) {
    this.approvalTasks = data[0].approvalTasks;
    this.valoremTasks = data[0].valoremTasks;
    this.argusTasks = data[0].argusTasks;
    this.valuationEventTasks = data[0].valuationEventTasks;
    this.generalTasks = data[1];
    this.generalApprovalHistoryList = data[2];
    this.userTasks = { approvalTasks: this.approvalTasks.filter(item => { return !item.dateApproved1 || !item.dateApproved2 || !item.dateApproved3 }), generalTasks: this.generalTasks, valoremTasks: this.valoremTasks, argusTasks: this.argusTasks, valuationEventTasks: this.valuationEventTasks }
  }

  /**
   * Update publish status and properties information after sync with argus is completed
   */
  onSyncWithArgusAction() {
    this.subscriptions.add(this._raciNavigationService.refreshTasks.subscribe(data => {
      if (data && this.activeTask.taskTypeId == TaskTypeEnum.ArgusSyncFunction) {
        this.subscriptions.add(this.getData().subscribe(response => {
          if (response) {
            const properties = response[0].argusTasks.filter(task => task.taskId == this.activeTask.taskId);

            this.argusLastSyncInfo = properties.sort(p => p.lastDataSync)[properties.length - 1];
            this.argusNumberOfSyncProp = properties.filter(p => p.dataStatus !== 'NOT SYNCED').length;
            this.approvalMatrixOfFunctionTask = properties;
          }
        }));
      }
    }));
  }

  /**
  * Gets the Action Stage enum
  */
  public get taskTypes(): typeof TaskTypeEnum {
    return TaskTypeEnum;
  }

  /**
   * Set approval matrix as active
   * @param data Approval matrix data
   */
  setApprovalMatrix(data: any) {
    this.approvalMatrixOfTask = data;
    this.generalTaskApprovalMatrix = null;
    this.approvalMatrixOfFunctionTask = null;
  }

  /**
   * Sets function task approval matrix as active
   * @param data Approval matrix data
   */
  setApprovalMatrixOfFunctionTask(data: any) {
    this.approvalMatrixOfFunctionTask = data;
    this.allowApproving = data && data.length > 0;

    this.approvalMatrixOfTask = null;
    this.generalTaskApprovalMatrix = null;
  }

  /**
   * Sets valuation setup task content as active
   */
  setValuationSetupTask() {
    this.approvalMatrixOfTask = null;
    this.generalTaskApprovalMatrix = null;
    this.approvalMatrixOfFunctionTask = null;
  }

  /**
   * Sets the general task approval matrix.
   * @param data The data representing the general task approval matrix.
   */
  setGeneralTaskApprovalMatrix(data: any) {
    this.userDashboardActiveTask = data.oid;
    this.isGeneralTaskSelected = true;
    this.generalTaskApprovalMatrix = data;
    this.approvalMatrixOfTask = null;
    this.approvalMatrixOfFunctionTask = null;
    this.selectedGeneralTaskProperty = null;
  }

  /**
   * Sets active task for user dashboard
   * @param taskId Task Id
   */
  public setActiveUserTask(taskId, isCompleted = false) {
    this.activeTask = this.approvalTasks?.find(task => task.taskId == taskId) ?? this.valoremTasks?.find(task => task.projectTaskId == taskId) ?? this.argusTasks?.find(task => task.taskId == taskId) ?? this.valuationEventTasks?.find(task => task.taskId == taskId);
    this.activePortfolio = this.activeTask?.openValuationEvents?.find(x => x.oid == this.activeTask?.portfolioId) ?? { "isFinalized": true, "oid": this.activeTask?.portfolioId }
    if (isCompleted) {
      this.activeTask.isCompleted = true;
      this.activeTask.taskActionStage = ActionStageEnum.Completed;
    }

    if (this.activeTask && this.activeTask.taskTypeId == null) {
      this.activeTask.taskTypeId = 1;
    }

    this.userDashboardActiveTask = taskId;
    this.isGeneralTaskSelected = false;
    this.hideApprovalMatrix = false;
    this.updateNavigationIcons(this.activeTask);
  }


  /**
   * Update navigation icons
   * @param task Active task
   */
  updateNavigationIcons(task: any) {
    this._raciNavigationService.resetNavigationIcons();

    switch (task?.taskTypeId) {
      case this.taskTypes.ValuationEventFunction:
        this._raciNavigationService.setActiveValuationEventIcons(task);
        break;
      case this.taskTypes.CloseValuationEvent:
        //Hide all icons
        break;
      case this.taskTypes.ValuationEffectiveDatesSetup:
        this._raciNavigationService.setActiveValuationEffectiveDatesSetupIcons(task);
        break;
      case this.taskTypes.ValoremFunction:
        this._raciNavigationService.setActiveValoremIcons();
        break;
      case this.taskTypes.Approval:
        this._raciNavigationService.setActiveApprovalIcons(task);
        break;
      case this.taskTypes.ArgusFunction:
        this._raciNavigationService.setActiveArgusIcons();
        break;
      case this.taskTypes.ArgusSyncFunction:
        this._raciNavigationService.setActiveArgusSyncIcons();
        break;
      case this.taskTypes.Undefined:
        this._raciNavigationService.setActiveUndeFinedTaskIcons(task);
        break;
      default:
        break;
    }
  }

  /**
   * Update status of user dashboard active task
   * @param status Task status
   */
  setUserDashboardCompleteTaskStatus() {
    this.subscriptions.add(this._userDashboardService.getApprovalTasks(this.currentUser?.OID).subscribe(data => {
      if (data) {
        const completedTask = this.approvalTasks.find(x => x.taskId == this.userDashboardActiveTask);
        this.refreshUserTasksList = !this.refreshUserTasksList;
        const unApprovedProperty = this.approvalTasks.filter(property => property.portfolioId == completedTask.taskValuationPortfolioId && (!property.dateApproved1 || !property.dateApproved2 || !property.dateApproved3) && property.taskId == completedTask.taskId)
        if (unApprovedProperty.length == 0) {
          this.hideApprovalMatrix = true;
          this._navigationService.updatePendingTasksCount(1);
        }
      }
    }));
  }

  /**
   * Sets the task that is selected for completion as active 
   * @param task Selected task (that is being completed)
   */
  onCompleteTask(task: any) {
    this.setActiveUserTask(task.projectTaskId, true);
  }

  /**
   * On refresh userdashboard tasks
   */
  onRefreshTasks() {
    this.subscriptions.add(this.getData().subscribe(data => {
      const activTaskId = this.activeTask?.projectTaskId ?? this.activeTask?.taskId;
      if (data) {
        this.setUserDashboardTasks(data);
        this.setActiveUserTask(activTaskId);
      }
    }));
  }

  /**
   * Update Selected General Task Property
   * @param selectedProperty Selected Property
   */
  updateSelectedGeneralTaskProperty(selectedProperty: any) {
    this.selectedGeneralTaskProperty = selectedProperty;
  }

  /**
   * Subscribe to groupPortfolioProperties event
   */
  groupPortfolioPropertiesBy() {
    this.subscriptions.add(this._raciNavigationService._groupPortfolioProperties.subscribe(data => {
      this.groupBy = data;
    }));
  }

  /**
   * Retrieves data from service
   */
  private getData() {
    const userApprovalTasks = this._userDashboardService.getApprovalTasks(this.currentUser?.OID);
    const generalTaskHistory = this._userDashboardService.getApprovalHistory();
    const userGeneralAssignments = this._userDashboardService.getUserGeneralAssignments();
    return forkJoin([userApprovalTasks, userGeneralAssignments, generalTaskHistory]);
  }

  /**
   * Listens for changes on displaying project details
   */
  onDisplayProjectDetailsBroadcast() {
    this.subscriptions.add(this._raciNavigationService._displayProjectDetails.subscribe(value => {
      this.isProjectDetailsDisplayed = value;
      this.isApprovalHistoryDisplayed = false;
    }));
  }

  /**
   * Listens for close button clicked
   */
  onCloseClickBroadcast(): Subscription {
    return this.eventBus.subscribeEvent("closeClicked", () => {
      this.isProjectDetailsDisplayed = false;
    });
  }

  /**
   * Display the publish to valorem view
   */
  onDisplayPublishToValorem(): void {
    this.subscriptions.add(this._raciNavigationService._onDisplayPublishToValorem.subscribe(value => {
      this.isPublishToValoremDisplayed = value;
    }));
  }

  /**
   *  Pulls data from argus
   */
  onPulldataFromArgus(): Subscription {
    return this.eventBus.subscribeEvent("getDataFromArgusDialog", () => {
      this.properties = [];
      this.showGetDataFromArgusDialog = true;
    });
  }

  /**
   * Listens for changes on displaying approval hhistory
   */
  onDisplayHistoryApprovalBroadcast() {
    this.subscriptions.add(this._raciNavigationService._onDisplayApprovalHistory.subscribe(value => {
      this.isApprovalHistoryDisplayed = value;
      if (this.isGeneralTaskActive) {
        this.generalTasksApprovalHistory = this.generalApprovalHistoryList;
      }
      this.isProjectDetailsDisplayed = false;
    }));
  }

  /**
   * Update publish to valorem information
   */
  onPublishInformationChange(publishData: any) {
    if (publishData?.publishStatus) {
      this.activeTask.valoremPublishInformation = publishData;
    } else if (this.activeTask.valoremPublishInformation && publishData.lastSyncStatus) {
      this.activeTask.valoremPublishInformation.lastSyncStatus = publishData.lastSyncStatus;
    }
  }

  /**
   * Close all side panels
   */
  closeAllPanes() {
    this.showGetDataFromArgusDialog = false;
    this.isSyncWithArgusDisplayed = false;
    this._raciNavigationService.onUpdateSyncWithArgusActiveStatus(false);
  }

  /**
   * Continue with second step of sync with Argus
   */
  continueWithArgus() {
    this.showGetDataFromArgusDialog = false;
    this.isSyncWithArgusDisplayed = true;
  }

  /**
   * Sync with Argus action
   * @param data Data
   */
  syncWithArgus(data: any) {
    this.selectedReportIds = data[0];
    this.properties = data[1];
    this.showGetDataFromArgusDialog = true;
  }

  /**
   * Listens for changes on displaying approval history
   */
  onDisplayTaskHistoryDialog() {
    this.subscriptions.add(this._raciNavigationService._onDisplayTaskApprovalHistoryDialog.subscribe(value => {
      this.shouldShowTaskApprovalHistory = value;
      this.isApprovalHistoryDisplayed = false;
      this.isProjectDetailsDisplayed = false;
    }))
  }

  /**
 * Show close valuation event listener
 */
  onDisplayCloseValuationEventDialog() {
    this.subscriptions.add(this._raciNavigationService._showCloseValuationEventDialog.subscribe(value => {
      this.showCloseValuationEventDialog = value;
    }));
  }

  /**
   * Selected close valuation event option
   */
  onSelectedCloseOption(option: number) {
    switch (option) {
      case 1:
        this.closeValEventDialogMessage = "raci.closeValuationEvent.closeFirstOptionDescription";
        this.closeValEventOptionSelected = 1;
        break;
      case 2:
        this.closeValEventDialogMessage = "raci.closeValuationEvent.closeSecondOptionDescription";
        this.closeValEventOptionSelected = 2;
        break;
      case 3:
        this.closeValEventDialogMessage = "raci.closeValuationEvent.closeThirdOptionDescription";
        this.closeValEventOptionSelected = 3;
        break;
      default:
        this.closeValEventDialogMessage = "";
        this.closeValEventOptionSelected = 0;
        break;
    }
  }

  /**
   * On close project
   */
  onCloseProject() {
    this.userTasks.approvalTasks.filter(task => task.projectId == this.activeTask.projectId).forEach(task => {
      task.taskActionStage = ActionStageEnum.Completed;
      this._userDashboardService.completeAllStepsAndTaskOfValuationEvent(task.portfolioId);
    })

    this.userTasks.argusTasks.filter(task => task.projectId == this.activeTask.projectId).forEach(task => {
      task.taskActionStage = ActionStageEnum.Completed;
      this._userDashboardService.completeAllStepsAndTaskOfValuationEvent(task.portfolioId);
    })

    this.userTasks.valoremTasks.filter(task => task.projectId == this.activeTask.projectId).forEach(task => {
      task.taskActionStage = ActionStageEnum.Completed;
      this._userDashboardService.completeAllStepsAndTaskOfValuationEvent(task.taskValuationPortfolioId);
    })

    this.userTasks.valuationEventTasks.filter(task => task.projectId == this.activeTask.projectId).forEach(task => {
      task.taskActionStage = ActionStageEnum.Completed;
      this._userDashboardService.completeAllStepsAndTaskOfValuationEvent(task.portfolioId);
    })

    this._userDashboardService.completeProjectSteps(this.activeTask.projectId);
    this._raciNavigationService.showCloseValuationEventDialog(false);
    this._userDashboardService.displayDataLoader(false);
  }

  /**
   * On select copy properties option
   */
  onSelectedCopyPropertiesOption(value: any) {
    this.copyProperties = value;
  }

  /**
   * On select copy fx tables option
   */
  onSelectedFxTablesOption(value: any) {
    this.copyFxTables = value;
  }

  /**
   * New valuation event name
   */
  onNewValuationName(value: any) {
    this.newValuationName = value;
  }

  /**
   * New valuation event date
   */
  onNewValuationDate(value: any) {
    this.newValuationDate = value;
  }

  /**
   * Listens for changes on displaying approval hhistory
   */
  onSetGeneralTaskActive(): void {
    this.subscriptions.add(this._raciNavigationService._isGeneralTaskActive.subscribe(value => {
      this.isGeneralTaskActive = value;
    }));
  }
}
